import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import * as actions from 'hellospa/page/prep-and-send/data/actions';
import * as previewActions from 'hellospa/components/signer-signature-document/preview/data/actions';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import * as types from 'hellospa/page/prep-and-send/data/types';
import * as editorTypes from 'hellospa/components/editor/types';
import useTriggers from 'hellospa/components/prep-and-send/trigger';
import MergeFieldsSection, { OVERFLOW } from 'hellospa/components/prep-and-send/review/merge-fields';
import { PageHeader } from 'hellospa/components/prep-and-send/header';
import { Dispatch } from 'hellospa/redux/types';
import { ConnectFormikToStepper } from 'hellospa/components/prep-and-send/formik-utils';
import styles from './index.scss';

const messages = defineMessages({
  header: {
    id: '',
    description: 'Dropbox header for Merge Fields',
    defaultMessage: 'Fill in merge fields',
  },
  mergeFieldOverflow: {
    id: '',
    description: 'Validation error for when a merge field contains too much text',
    defaultMessage: 'Text has exceeded the current field size. Increase the field size in your template or reduce the amount of text.',
  },
});

const MergeFields: types.WorkflowComponent = ({
  onSaveActions,
  formRef,
  triggers,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch<Dispatch>();

  const mergeFields = useSelector(selectors.getAllMergeFields);

  const initialValues = {
    mergeFields,
  };
  type InitialValues = typeof initialValues
  const triggerOptions = {
    load: () => { },
    submit: () => dispatch(actions.workflowOnSave(onSaveActions)),
    done: () => { },
  };

  const [Triggers, setStage] = useTriggers(triggers, triggerOptions);

  // Not sure how to make typescript happy with this schema
  const schema: Yup.ObjectSchema<InitialValues> | null = React.useMemo(
    (): Yup.ObjectSchema<any> | null => {
      const schema = Yup.object();

      return schema.clone().shape({
        mergeFields: Yup.array(Yup.mixed().test(
          'overflow',
          ({ label }) => intl.formatMessage(messages.mergeFieldOverflow, { label }),
          function checkOverflow(mergeField: types.MergeField) {
            // Validation can't run here because there is no access to DOM to
            // measure the text. <TextInput maintains this fake `.error`
            // property based on useSignerTextInput
            if (mergeField.error === OVERFLOW) {
              // This needs to match the path used in TextInput (./merge-fields)
              // so that it gets marked as touched as the user leaves the field.
              return this.createError({ path: `[${this.path}.field]` });
            }

            return true;
          },
        )),
      });
    },
    // eslint-disable-next-line max-len
    [intl],
  );

  const save = React.useCallback(async (values: InitialValues) => {
    values.mergeFields.map(
      (field) => dispatch(actions.updateField(field)),
    );
  }, [dispatch]);

  const submit = React.useCallback(async (values: InitialValues) => {
    const normalizedValues: InitialValues = schema ? schema.cast(values) : values;
    await save(normalizedValues);

    // This won't resolve until the submit callback completes. This keeps the
    // promise chain intact from the onClick in the stepper button all the way
    // through making the request to the server in workflowOnSave.
    await setStage.submit();
  }, [save, schema, setStage]);

  const onFieldChange = (fieldData: editorTypes.TextField | editorTypes.CheckboxField) => {
    let fieldUpdate = null;
    /**
     * We need to check for type in addition to the "checked" attribute because
     * on the first time this field is changed, "checked" does not exist in fieldData yet.
     */
    if ('checked' in fieldData || fieldData.type === 'checkbox') {
      fieldUpdate = {
        id: fieldData.id,
        checked: !fieldData.checked,
      };
    } else if ('value' in fieldData) {
      fieldUpdate = {
        id: fieldData.id,
        value: fieldData.value,
      };
    }
    if (fieldUpdate) {
      dispatch(previewActions.updatePreviewField(fieldData.id, fieldUpdate));
    }
  };

  return (
    <div>
      <PageHeader text={intl.formatMessage(
        messages.header,
      )} />
      <Formik
        initialValues={initialValues}
        onSubmit={(values) => submit(values)}
        validateOnChange={false}
        validationSchema={schema}
        validateOnMount={true}
        enableReinitialize={true}
        innerRef={types.useFormikRef(formRef)}
        // eslint-disable-next-line react/no-children-prop
        children={() => (
          <Form>
            <ConnectFormikToStepper />
            <Triggers />
            <div className={styles.mergeFieldsSection}>
              <MergeFieldsSection onFieldChange={onFieldChange} />
            </div>
          </Form>
        )} />
    </div>
  );
};

MergeFields.getText = (workflow, state) => {
  const msg = defineMessages({
    title: {
      id: '',
      description: 'title of page, also used in Stepper tab',
      defaultMessage: 'Merge fields',
    },
    titleTemplate: {
      id: '',
      description: 'title of page, also used in Stepper tab',
      defaultMessage: 'Merge fields',
    },
    dropboxTitle: {
      id: '',
      description: 'title of page, also used in Stepper tab',
      defaultMessage: 'Merge fields',
    },
    next: {
      id: '',
      description: 'Label for button that advances user to the next step in the current workflow',
      defaultMessage: 'Next',
    },
    sendDocument: {
      id: '',
      description: 'Label for button that advances user to the next step in the current workflow',
      defaultMessage: 'Send Document',
    },
    sendForSignature: {
      id: '',
      description: 'Label for button that advances user to the next step in the current workflow',
      defaultMessage: 'Send for signature',
    },
    saveTemplate: {
      id: '',
      description: 'Label for button that advances user to the next step in the current workflow',
      defaultMessage: 'Save template',
    },
    send: {
      id: '',
      description: 'Label for button that advances user to the next step in the current workflow',
      defaultMessage: 'Send',
    },
    save: {
      id: '',
      description: 'Label for button that advances user to the next step in the current workflow',
      defaultMessage: 'Save',
    },
  });

  let result: types.PageText = {
    title: msg.title,
    next: msg.next,
  };

  if (workflow.tags.includes(types.WorkflowTags.CreateTemplate)
    || workflow.tags.includes(types.WorkflowTags.CreateTemplateLink)
  ) {
    result = {
      ...result,
      title: msg.titleTemplate,
    };
  }

  if (selectors.isMeOnly(state)) {
    result = {
      ...result,
      next: msg.sendDocument,
      shortNext: msg.send,
    };
  }

  return result;
};

export default MergeFields;
